import React, { useContext, useRef } from 'react';
import AppContext from '../../AppContext';
import { useInputKeyboardHandler } from '../../hooks/useInputKeyboardHandler';
import { parseNumberInput, getNumberInputValue } from '../../utils/numberInputHelpers';

/**
 * Number input component with automatic keyboard handling and validation
 *
 * Features:
 * - Auto-scrolls into view when focused (prevents keyboard covering input)
 * - Theme-aware styling from AppContext
 * - Prevents leading zeros using numberInputHelpers
 * - Supports min/max validation and decimal/integer modes
 * - Prevents scroll-to-change on desktop (onWheel blur)
 *
 * @param {Object} props
 * @param {string|number} props.value - Current value
 * @param {Function} props.onChange - Change handler receives parsed value (not event)
 * @param {Object} props.options - Number input validation options
 * @param {number} props.options.min - Minimum allowed value
 * @param {number} props.options.max - Maximum allowed value
 * @param {boolean} props.options.allowDecimals - Allow decimal values
 * @param {boolean} props.options.allowEmpty - Allow empty string
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.style - Additional styles to merge with defaults
 * @param {Function} props.onFocus - Additional onFocus handler
 * @param {Object} props.keyboardOptions - Options for keyboard handler
 */
const KeyboardAwareNumberInput = ({
  value,
  onChange,
  options = {},
  placeholder = '0',
  style = {},
  onFocus,
  keyboardOptions = {},
  ...props
}) => {
  const { theme } = useContext(AppContext);
  const { inputProps } = useInputKeyboardHandler(keyboardOptions);
  const inputRef = useRef(null);

  // Handle change with number parsing
  const handleChange = (e) => {
    const parsedValue = parseNumberInput(e.target.value, options);
    onChange(parsedValue);
  };

  // Merge onFocus handlers
  const handleFocus = (e) => {
    if (inputProps.onFocus) inputProps.onFocus(e);
    if (onFocus) onFocus(e);
  };

  // Prevent scroll-to-change on desktop
  const handleWheel = (e) => {
    e.target.blur();
  };

  // Default theme-aware styles
  const defaultStyles = {
    padding: theme?.spacing?.small || '0.5rem',
    fontSize: theme?.typography?.body || '1rem',
    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
    borderRadius: theme?.spacing?.borderRadius || '4px',
    backgroundColor: theme?.colors?.surface || '#fff',
    color: theme?.colors?.text || '#000',
    outline: 'none',
    width: '100%',
    ...style
  };

  return (
    <input
      ref={inputRef}
      type="number"
      step={options.allowDecimals ? '0.01' : '1'}
      value={getNumberInputValue(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onWheel={handleWheel}
      placeholder={placeholder}
      style={defaultStyles}
      {...props}
    />
  );
};

export default KeyboardAwareNumberInput;
